export type SocialScoreBreakdown = {
  baseActivityPoints: number
  engagementQualityPoints: number
  consistencyPoints: number
  platformDiversityPoints: number
  penaltyPoints: number
}

export type SocialScoreMetrics = {
  activePlatformCount: number
  originalContentCount: number
  reshareCount: number
  commentCount: number
  highEngagementContentCount: number
  activeWeekCount: number
}

export type PlatformBreakdownItem = {
  platform: string
  points: number
  contents: number
}

export type TraderSocialScore = {
  traderId: string
  periodKey: string
  socialRank: number | null
  traderCount: number
  totalSocialScore: number
  isFinalized: boolean
  breakdown: SocialScoreBreakdown
  metrics: SocialScoreMetrics
  platformBreakdown: PlatformBreakdownItem[]
}

export type SocialContentItem = {
  id: string
  platform: string
  platformContentId: string
  contentUrl?: string | null
  contentType: string
  captionText?: string | null
  publishedAt: string
  reviewStatus: string
  eligibilityReason?: string | null
  likesCount: number
  commentsCount: number
  sharesCount: number
  basePoints: number
  engagementBonusPoints: number
  totalPoints: number
}

/** Map performance period to social-rank monthly key (YYYY-MM). */
export function socialRankPeriodKey(period: string): string | null {
  const now = new Date()
  if (period === 'this_month') {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  }
  if (period === 'last_month') {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }
  return null
}

/** Backend may return snake_case; normalize for UI. */
export function normalizeSocialScore(raw: Record<string, unknown>): TraderSocialScore {
  const breakdown = (raw.breakdown ?? {}) as Record<string, unknown>
  const metrics = (raw.metrics ?? {}) as Record<string, unknown>
  const rawPlatformBreakdown = raw.platform_breakdown ?? raw.platformBreakdown
  const platformBreakdown: Record<string, unknown>[] = Array.isArray(rawPlatformBreakdown)
    ? (rawPlatformBreakdown as Record<string, unknown>[])
    : []

  return {
    traderId: String(raw.trader_id ?? raw.traderId ?? ''),
    periodKey: String(raw.period_key ?? raw.periodKey ?? ''),
    socialRank:
      raw.social_rank != null
        ? Number(raw.social_rank)
        : raw.socialRank != null
        ? Number(raw.socialRank)
        : null,
    traderCount: Number(raw.trader_count ?? raw.traderCount ?? 0),
    totalSocialScore: Number(raw.total_social_score ?? raw.totalSocialScore ?? 0),
    isFinalized: Boolean(raw.is_finalized ?? raw.isFinalized),
    breakdown: {
      baseActivityPoints: Number(
        breakdown.base_activity_points ?? breakdown.baseActivityPoints ?? 0
      ),
      engagementQualityPoints: Number(
        breakdown.engagement_quality_points ?? breakdown.engagementQualityPoints ?? 0
      ),
      consistencyPoints: Number(breakdown.consistency_points ?? breakdown.consistencyPoints ?? 0),
      platformDiversityPoints: Number(
        breakdown.platform_diversity_points ?? breakdown.platformDiversityPoints ?? 0
      ),
      penaltyPoints: Number(breakdown.penalty_points ?? breakdown.penaltyPoints ?? 0),
    },
    metrics: {
      activePlatformCount: Number(
        metrics.active_platform_count ?? metrics.activePlatformCount ?? 0
      ),
      originalContentCount: Number(
        metrics.original_content_count ?? metrics.originalContentCount ?? 0
      ),
      reshareCount: Number(metrics.reshare_count ?? metrics.reshareCount ?? 0),
      commentCount: Number(metrics.comment_count ?? metrics.commentCount ?? 0),
      highEngagementContentCount: Number(
        metrics.high_engagement_content_count ?? metrics.highEngagementContentCount ?? 0
      ),
      activeWeekCount: Number(metrics.active_week_count ?? metrics.activeWeekCount ?? 0),
    },
    platformBreakdown: platformBreakdown.map((item) => ({
      platform: String(item.platform ?? ''),
      points: Number(item.points ?? 0),
      contents: Number(item.contents ?? 0),
    })),
  }
}

export const PLATFORM_LABELS: Record<string, string> = {
  x: 'X',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
}
